import React from 'react';
import PropTypes from 'prop-types';
import { Translate } from 'react-localize-redux';
import Card from '@mui/material/Card';
import CardContent from '@mui/material/CardContent';
import CardActions from '@mui/material/CardActions';
import { TextField, SwitchField, Button } from '../../components/widgets';
import css from './EditOrg.css';

const EditOrg = props => {
    const { fields, edit, editChange, editConfirm, readOnly, segments = [] } = props;
    const allowMultipleAudiences = edit.allow_multiple_audiences;
    const maxMultipleAudiences = edit.multiple_audiences_max_amount || 5;
    const segmentsPerUan = allowMultipleAudiences && edit.segments_per_uan;
    const visibleFields = fields.filter(field => field.ui_type !== 'hidden');

    const normalizeEntries = rawValue => {
        if (Array.isArray(rawValue)) {
            return rawValue.map(item => {
                if (item && typeof item === 'object') {
                    return {
                        value: item.value || '',
                        uan_id: item.uan_id || null,
                        segment_ids: item.segment_ids || [],
                    };
                }
                return { value: item || '', uan_id: null, segment_ids: [] };
            });
        }
        if (rawValue && typeof rawValue === 'object') {
            return [
                {
                    value: rawValue.value || '',
                    uan_id: rawValue.uan_id || null,
                    segment_ids: rawValue.segment_ids || [],
                },
            ];
        }
        if (rawValue) {
            return [{ value: rawValue, uan_id: null, segment_ids: [] }];
        }
        return [{ value: '', uan_id: null, segment_ids: [] }];
    };

    const getFieldEntries = field => normalizeEntries(edit.distribution.config[field.name]);

    const getRowCount = () => {
        if (!allowMultipleAudiences) {
            return 1;
        }
        const lengths = visibleFields.map(field => getFieldEntries(field).length);
        const maxLength = Math.max.apply(Math, lengths.concat([1]));
        return maxLength;
    };

    const updateFieldValueAtIndex = (field, index, value) => {
        const currentEntries = getFieldEntries(field).slice();
        while (currentEntries.length < getRowCount()) {
            currentEntries.push({ value: '', uan_id: null, segment_ids: [] });
        }
        currentEntries[index] = {
            value,
            uan_id: currentEntries[index].uan_id,
            segment_ids: currentEntries[index].segment_ids || [],
        };
        editChange(field.name, currentEntries, true);
    };

    // Segment assignments live on every field row so the payload stays uniform; the first
    // visible field is the one the UI reads back.
    const getAssignedSegmentIds = rowIndex => {
        const primaryField = visibleFields[0];
        if (!primaryField) {
            return [];
        }
        return (getFieldEntries(primaryField)[rowIndex] || {}).segment_ids || [];
    };

    const setAssignedSegmentIds = (rowIndex, segmentIds) => {
        visibleFields.forEach(field => {
            const currentEntries = getFieldEntries(field).slice();
            while (currentEntries.length < getRowCount()) {
                currentEntries.push({ value: '', uan_id: null, segment_ids: [] });
            }
            currentEntries[rowIndex] = { ...currentEntries[rowIndex], segment_ids: segmentIds };
            editChange(field.name, currentEntries, true);
        });
    };

    const toggleSegment = (rowIndex, segmentId, checked) => {
        const assigned = getAssignedSegmentIds(rowIndex);
        const nextAssigned = checked ? assigned.concat([segmentId]) : assigned.filter(id => id !== segmentId);
        setAssignedSegmentIds(rowIndex, nextAssigned);
    };

    // A segment can only belong to one API key, so show where it is already taken.
    const getSegmentOwnerRow = segmentId => {
        for (let rowIndex = 0; rowIndex < getRowCount(); rowIndex += 1) {
            if (getAssignedSegmentIds(rowIndex).includes(segmentId)) {
                return rowIndex;
            }
        }
        return -1;
    };

    const addRow = () => {
        const rowCount = getRowCount();
        if (rowCount >= maxMultipleAudiences) {
            return;
        }
        visibleFields.forEach(field => {
            const currentEntries = getFieldEntries(field);
            editChange(field.name, currentEntries.concat([{ value: '', uan_id: null, segment_ids: [] }]), true);
        });
    };

    const removeRow = index => {
        visibleFields.forEach(field => {
            const currentEntries = getFieldEntries(field);
            const nextEntries = currentEntries.filter((_, idx) => idx !== index);
            editChange(
                field.name,
                nextEntries.length ? nextEntries : [{ value: '', uan_id: null, segment_ids: [] }],
                true
            );
        });
    };
    return (
        <Card className={css.container}>
            <CardContent className={css.cardContent}>
                <div style={{ textAlign: 'right' }}>
                    <SwitchField
                        label="Active"
                        checked={edit.distribution.active}
                        disabled={readOnly}
                        onChange={checked => {
                            editChange('active', checked);
                        }}
                    />
                </div>
                {allowMultipleAudiences ? (
                    <div className={css.multipleAudienceContainer}>
                        {Array.from({ length: getRowCount() }).map((_, rowIndex) => (
                            <div key={`audience-row-${rowIndex}`} className={css.multipleAudienceRow}>
                                <div className={css.multipleAudienceFields}>
                                    {visibleFields.map(field => (
                                        <TextField
                                            key={`${field.name}-${rowIndex}`}
                                            label={rowIndex === 0 ? field.display : ''}
                                            value={(getFieldEntries(field)[rowIndex] || {}).value || ''}
                                            password={field.ui_type === 'password'}
                                            onChange={value => updateFieldValueAtIndex(field, rowIndex, value)}
                                            autoCompleteOff
                                            disabled={readOnly}
                                        />
                                    ))}
                                    {segmentsPerUan && (
                                        <div className={css.segmentAssignment}>
                                            <div className={css.segmentAssignmentLabel}>
                                                <Translate id="STATIC.PAGES.AUDIENCES.SEGMENTS_FOR_API_KEY" />
                                            </div>
                                            {segments.length ? (
                                                <div className={css.segmentAssignmentList}>
                                                    {segments.map(seg => {
                                                        const ownerRow = getSegmentOwnerRow(seg.id);
                                                        const isMine = ownerRow === rowIndex;
                                                        const takenElsewhere = ownerRow !== -1 && !isMine;
                                                        return (
                                                            // eslint-disable-next-line jsx-a11y/label-has-associated-control
                                                            <label
                                                                key={seg.id}
                                                                className={`${css.segmentOption} ${
                                                                    takenElsewhere ? css.segmentOptionTaken : ''
                                                                }`}
                                                                title={
                                                                    takenElsewhere
                                                                        ? `Already assigned to API key #${ownerRow + 1}`
                                                                        : seg.name
                                                                }
                                                            >
                                                                <input
                                                                    type="checkbox"
                                                                    checked={isMine}
                                                                    disabled={readOnly || takenElsewhere}
                                                                    onChange={e =>
                                                                        toggleSegment(
                                                                            rowIndex,
                                                                            seg.id,
                                                                            e.target.checked
                                                                        )
                                                                    }
                                                                />
                                                                {seg.name}
                                                            </label>
                                                        );
                                                    })}
                                                </div>
                                            ) : (
                                                <div className={css.segmentAssignmentEmpty}>
                                                    <Translate id="STATIC.PAGES.AUDIENCES.NO_SEGMENTS_AVAILABLE" />
                                                </div>
                                            )}
                                        </div>
                                    )}
                                </div>
                                {!readOnly && (
                                    <div className={css.multipleAudienceActions}>
                                        <Button
                                            type="secondary"
                                            onClick={() => removeRow(rowIndex)}
                                            disabled={getRowCount() === 1}
                                        >
                                            <Translate id="STATIC.PAGES.AUDIENCES.REMOVE_API_KEY" />
                                        </Button>
                                    </div>
                                )}
                            </div>
                        ))}
                        {!readOnly && (
                            <div className={css.addMultipleAudienceRow}>
                                <Button
                                    type="secondary"
                                    onClick={addRow}
                                    disabled={getRowCount() >= maxMultipleAudiences}
                                >
                                    <Translate id="STATIC.PAGES.AUDIENCES.ADD_API_KEY" />
                                </Button>
                            </div>
                        )}
                    </div>
                ) : (
                    visibleFields.map(field => (
                        <TextField
                            key={field.name}
                            label={field.display}
                            value={edit.distribution.config[field.name]}
                            password={field.ui_type === 'password'}
                            onChange={value => {
                                editChange(field.name, value, true);
                            }}
                            autoCompleteOff
                            disabled={readOnly}
                        />
                    ))
                )}
            </CardContent>
            {!readOnly && (
                <CardActions className={css.cardActions}>
                    <Button onClick={editConfirm}>
                        <Translate id="STATIC.BUTTONS.SAVE" />
                    </Button>
                </CardActions>
            )}
        </Card>
    );
};

EditOrg.propTypes = {
    segments: PropTypes.arrayOf(
        PropTypes.shape({
            id: PropTypes.number.isRequired,
            name: PropTypes.string,
        })
    ),
    edit: PropTypes.shape({
        allow_multiple_audiences: PropTypes.bool,
        multiple_audiences_max_amount: PropTypes.number,
        segments_per_uan: PropTypes.bool,
    }),
};

export default EditOrg;
